import { ColumnDef, flexRender } from '@tanstack/react-table';
import { OrderData, OrderDataRegion } from 'api/dashboard/_request';
import classNames from 'classnames';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import {
  TopRegionsTableDataType,
  topRegionsTableData
} from 'data/TopRegionsTableData';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import team15 from 'assets/img/team/avatar.webp';

const countryCodeMap: Record<string, string> = {
  India: 'in',
  'Saudi Arabia': 'sa',
  UAE: 'ae',
  USA: 'us',
  Germany: 'de',
  France: 'fr',
  UK: 'gb',
  Canada: 'ca',
  default: 'ae'
};

const columns: ColumnDef<OrderDataRegion>[] = [
  {
    header: 'COUNTRY',
    accessorKey: 'country',
    cell: ({ row }) => {
      const serial = row.index + 1;
      const { country } = row.original;
      const countryCode =
        countryCodeMap[country || ''] || countryCodeMap.default;
      const flagUrl = `https://flagcdn.com/w40/${countryCode}.png`;

      return (
        <div className="d-flex align-items-center">
          <h6 className="mb-0 me-3">{serial}.</h6>
          <img
            src={flagUrl}
            alt={country || 'UAE'}
            width={20}
            className="me-2"
          />
          <p className="mb-0 text-primary fw-bold fs-9">{country || 'UAE'}</p>
        </div>
      );
    },
    meta: {
      headerProps: { style: { width: '32%' }, className: 'ps-0' },
      cellProps: { className: 'white-space-nowrap align-middle ps-0' }
    }
  },
  {
    header: 'ORDERS',
    accessorKey: 'totalOrders',
    cell: ({ row }) => <h6 className="mb-0">{row.original.totalOrders}</h6>,
    meta: {
      headerProps: {
        style: { width: '16%' },
        className: 'text-end align-middle'
      },
      cellProps: { className: 'text-end align-middle' }
    }
  },
  {
    header: 'CUSTOMER',
    accessorKey: 'totalUsers',
    cell: ({ row }) => <h6 className="mb-0">{row.original.totalUsers}</h6>,
    meta: {
      headerProps: {
        style: { width: '16%' },
        className: 'text-end align-middle'
      },
      cellProps: { className: 'text-end align-middle' }
    }
  },
  {
    header: 'PRODUCTS',
    accessorKey: 'totalProducts',
    cell: ({ row }) => <h6 className="mb-0">{row.original.totalProducts}</h6>,
    meta: {
      headerProps: {
        style: { width: '16%' },
        className: 'text-end align-middle'
      },
      cellProps: { className: 'text-end align-middle' }
    }
  }
];

const EcomTopRegionsTable = ({ data }: { data?: OrderDataRegion[] }) => {
  const table = useAdvanceTable({
    data: data ?? [],
    columns,
    pageSize: 5,
    pagination: true,
    selectionColumnWidth: '30px',
    sortable: true
  });

  const { getRowModel, getFlatHeaders } = table;

  const totalOrders =
    data?.reduce((sum, row) => sum + (row.totalOrders || 0), 0) ?? 0;
  const totalProducts =
    data?.reduce((sum, row) => sum + (row.totalProducts || 0), 0) ?? 0;
  const totalUsers =
    data?.reduce((sum, row) => sum + (row.totalUsers || 0), 0) ?? 0;

  return (
    <AdvanceTableProvider {...table}>
      {/* <Scrollbar autoHeight autoHeightMax="100%"> */}
      <div className="scrollbar">
        <Table className="fs-10 mb-0 border-top border-translucent scrollbar">
          <thead>
            <tr>
              {getFlatHeaders().map(header => {
                return (
                  <th
                    key={header.id}
                    {...header.column.columnDef.meta?.headerProps}
                    className={classNames(
                      'sort',
                      header.column.columnDef.meta?.headerProps?.className,
                      {
                        desc: header.column.getIsSorted() === 'desc',
                        asc: header.column.getIsSorted() === 'asc'
                      }
                    )}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td></td>
              <td className="py-4 text-end">
                <h4 className="mb-0 fw-normal">{totalOrders}</h4>
              </td>
              <td className="py-4 text-end">
                <h4 className="mb-0 fw-normal">{totalProducts}</h4>
              </td>
              <td className="py-4 text-end">
                <h4 className="mb-0 fw-normal">{totalUsers}</h4>
              </td>
            </tr>

            {getRowModel().rows.map(row => (
              <tr key={row.id}>
                {row.getVisibleCells().map(cell => (
                  <td key={cell.id} {...cell.column.columnDef.meta?.cellProps}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      <AdvanceTableFooter className="gx-0" navBtn showViewAllBtn={false} />
      {/* </Scrollbar> */}
    </AdvanceTableProvider>
  );
};

export default EcomTopRegionsTable;
